import { useState } from "react";
import { Eye, Plus } from "lucide-react";

import {
  type CarrierVehicle,
  useCarrierMe,
  useCarrierVehicles,
} from "@/api/carrier";
import {
  EmptyRow,
  ErrorRow,
  LoadingRows,
  TablePager,
  TableToolbar,
  usePagedState,
} from "@/components/data-table";
import { DetailDrawer } from "@/components/detail-drawer";
import { PageHeader } from "@/components/page-header";
import { StatusBadge } from "@/components/status-badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CreateVehicleDialog } from "@/features/create-vehicle-dialog";
import { EditVehicleDialog } from "@/features/edit-vehicle-dialog";
import { hasHttpStatus } from "@/lib/api-error";
import {
  vehicleCapacityDisplay,
  vehicleDisplayName,
  vehicleOperatorDisplayName,
} from "@/lib/vehicle-display";

const COLUMNS = 7;

const STATUS_FILTERS = [
  "AVAILABLE",
  "IN_SERVICE",
  "MAINTENANCE",
  "OUT_OF_SERVICE",
] as const;

function vehicleLabel(vehicle: CarrierVehicle): string {
  return vehicle.licensePlate ?? vehicle.code;
}

function dispatchCenterName(vehicle: CarrierVehicle): string {
  return (
    vehicle.dispatchCenter?.name ??
    vehicle.depot?.name ??
    "Unknown Dispatch Center"
  );
}

/**
 * Vehicles screen — carrier-portal P1.3. The server supports pagination and
 * status filtering; fleet-wide search traverses that scoped live endpoint.
 */
export default function VehiclesScreen() {
  const carrierMe = useCarrierMe();
  const canCreate = carrierMe.data?.roles.includes("CARRIER_OWNER") ?? false;
  const [status, setStatus] = useState("");
  const { page, pageSize, search, setPage, setPageSize, setSearch, resetPage } =
    usePagedState();
  const { data, isLoading, error, refetch } = useCarrierVehicles({
    page,
    limit: pageSize,
    ...(search.trim() && { q: search.trim() }),
    ...(status && { status }),
  });
  const [editing, setEditing] = useState<CarrierVehicle | null>(null);
  const [details, setDetails] = useState<CarrierVehicle | null>(null);
  const [creating, setCreating] = useState(false);
  const rows = data?.data ?? [];
  const normalizedSearch = search.trim();
  const showRows = !isLoading && !error;
  const forbidden = hasHttpStatus(error, 403);
  const emptyMessage = normalizedSearch
    ? "No vehicles match your search."
    : status
      ? "No vehicles match the selected status."
      : "No vehicles yet.";

  return (
    <div className="carrier-page">
      <PageHeader
        title="Vehicles"
        actions={
          canCreate ? (
            <Button size="touch" onClick={() => setCreating(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add vehicle
            </Button>
          ) : null
        }
      />

      <Card className="overflow-hidden">
        <div className="border-b p-3 sm:p-4">
          <TableToolbar
            className="mb-0"
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search vehicles..."
            searchAriaLabel="Search vehicles"
          >
            <Select
              value={status || "ALL"}
              onValueChange={(value) => {
                resetPage();
                setStatus(value === "ALL" ? "" : value);
              }}
            >
              <SelectTrigger
                aria-label="Filter by status"
                className="w-full sm:w-44"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All statuses</SelectItem>
                {STATUS_FILTERS.map((item) => (
                  <SelectItem key={item} value={item}>
                    {item.replace(/_/g, " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </TableToolbar>
        </div>

        <div className="md:hidden">
          {isLoading && (
            <div
              role="status"
              className="p-6 text-center text-sm text-zinc-500"
            >
              Loading vehicles...
            </div>
          )}
          {error && (
            <div role="alert" className="space-y-3 p-6 text-center">
              <p className="text-sm text-destructive">
                {forbidden
                  ? "You do not have permission to view vehicles."
                  : "Failed to load vehicles."}
              </p>
              {!forbidden && (
                <Button
                  variant="outline"
                  size="sm-touch"
                  onClick={() => refetch()}
                >
                  Retry
                </Button>
              )}
            </div>
          )}
          {showRows && rows.length === 0 && (
            <p className="p-6 text-center text-sm text-zinc-500">
              {emptyMessage}
            </p>
          )}
          {showRows && rows.length > 0 && (
            <ul aria-label="Vehicle summaries" className="divide-y">
              {rows.map((vehicle) => {
                const label = vehicleLabel(vehicle);
                return (
                  <li key={vehicle.id} aria-label={label} className="p-3">
                    <div className="flex items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-mono text-xs font-semibold">
                          {label}
                        </p>
                        <p className="truncate text-xs text-zinc-500">
                          {vehicleDisplayName(vehicle) ||
                            "Vehicle details unavailable"}
                        </p>
                      </div>
                      <StatusBadge status={vehicle.status} />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-touch"
                        aria-label={`View ${label} details`}
                        onClick={() => setDetails(vehicle)}
                      >
                        <Eye />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm-touch"
                        onClick={() => setEditing(vehicle)}
                      >
                        Edit
                      </Button>
                    </div>
                    <dl className="mt-2 grid grid-cols-3 gap-x-3 text-xs">
                      <div className="min-w-0">
                        <dt className="text-[10px] uppercase text-zinc-400">
                          Type
                        </dt>
                        <dd>
                          <StatusBadge status={vehicle.type} />
                        </dd>
                      </div>
                      <div className="min-w-0">
                        <dt className="text-[10px] uppercase text-zinc-400">
                          Center
                        </dt>
                        <dd className="truncate">
                          {dispatchCenterName(vehicle)}
                        </dd>
                      </div>
                      <div className="min-w-0">
                        <dt className="text-[10px] uppercase text-zinc-400">
                          Operator
                        </dt>
                        <dd className="truncate">
                          {vehicleOperatorDisplayName(vehicle) ||
                            "No operator assigned"}
                        </dd>
                      </div>
                    </dl>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="whitespace-nowrap">Plate</TableHead>
                <TableHead className="whitespace-nowrap">Type</TableHead>
                <TableHead className="whitespace-nowrap">Status</TableHead>
                <TableHead className="whitespace-nowrap">
                  Make / model / year
                </TableHead>
                <TableHead className="whitespace-nowrap">
                  Dispatch center
                </TableHead>
                <TableHead className="whitespace-nowrap">
                  Assigned operator
                </TableHead>
                <TableHead className="w-12" aria-label="Actions" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <LoadingRows columns={COLUMNS} label="Loading vehicles..." />
              )}
              {error && (
                <ErrorRow
                  columns={COLUMNS}
                  message={
                    forbidden
                      ? "You do not have permission to view vehicles."
                      : "Failed to load vehicles."
                  }
                  onRetry={forbidden ? undefined : () => refetch()}
                />
              )}
              {showRows && rows.length === 0 && (
                <EmptyRow columns={COLUMNS}>{emptyMessage}</EmptyRow>
              )}
              {showRows &&
                rows.map((vehicle) => {
                  const label = vehicleLabel(vehicle);
                  return (
                    <TableRow
                      key={vehicle.id}
                      className="cursor-pointer"
                      tabIndex={0}
                      aria-label={`View details for ${label}`}
                      onClick={() => setDetails(vehicle)}
                      onKeyDown={(event) => {
                        if (event.target !== event.currentTarget) return;
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setDetails(vehicle);
                        }
                      }}
                    >
                      <TableCell className="font-mono text-xs">
                        {label}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={vehicle.type} />
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={vehicle.status} />
                      </TableCell>
                      <TableCell>
                        {vehicleDisplayName(vehicle) ||
                          "Vehicle details unavailable"}
                      </TableCell>
                      <TableCell>{dispatchCenterName(vehicle)}</TableCell>
                      <TableCell>
                        {vehicleOperatorDisplayName(vehicle) ||
                          "No operator assigned"}
                      </TableCell>
                      <TableCell onClick={(event) => event.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="sm-touch"
                          onClick={() => setEditing(vehicle)}
                        >
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </div>

        {data && !normalizedSearch && (
          <div className="border-t p-3">
            <TablePager
              page={data.metadata.page}
              pageSize={data.metadata.limit}
              total={data.metadata.total}
              totalPages={data.metadata.totalPages}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              disabled={isLoading}
            />
          </div>
        )}
      </Card>

      {editing && (
        <EditVehicleDialog
          open={!!editing}
          onOpenChange={(open) => {
            if (!open) setEditing(null);
          }}
          vehicle={editing}
        />
      )}

      <DetailDrawer
        open={details !== null}
        onOpenChange={(open) => {
          if (!open) setDetails(null);
        }}
        title={details ? `Vehicle ${vehicleLabel(details)}` : "Vehicle"}
        widthClassName="sm:max-w-xl"
      >
        {details && (
          <div className="space-y-4">
            <dl className="grid grid-cols-[max-content_1fr] gap-x-5 gap-y-2 text-sm">
              <dt className="text-muted-foreground">Code</dt>
              <dd className="font-mono break-all">{details.code}</dd>
              <dt className="text-muted-foreground">License plate</dt>
              <dd className="font-mono break-all">
                {details.licensePlate ?? "No license plate"}
              </dd>
              <dt className="text-muted-foreground">Type</dt>
              <dd>
                <StatusBadge status={details.type} />
              </dd>
              <dt className="text-muted-foreground">Status</dt>
              <dd>
                <StatusBadge status={details.status} />
              </dd>
              <dt className="text-muted-foreground">VIN</dt>
              <dd className="font-mono break-all">{details.vin ?? "No VIN"}</dd>
              <dt className="text-muted-foreground">Capacity</dt>
              <dd className="whitespace-pre-wrap break-words font-mono text-xs">
                {vehicleCapacityDisplay(details.capacity)}
              </dd>
              <dt className="text-muted-foreground">Make / model / year</dt>
              <dd>
                {vehicleDisplayName(details) || "Vehicle details unavailable"}
              </dd>
              <dt className="text-muted-foreground">Dispatch center</dt>
              <dd>{dispatchCenterName(details)}</dd>
              <dt className="text-muted-foreground">Assigned operator</dt>
              <dd>
                {vehicleOperatorDisplayName(details) || "No operator assigned"}
              </dd>
            </dl>
            <div className="flex justify-end">
              <Button
                type="button"
                size="touch"
                onClick={() => {
                  setDetails(null);
                  setEditing(details);
                }}
              >
                Edit vehicle
              </Button>
            </div>
          </div>
        )}
      </DetailDrawer>

      <CreateVehicleDialog open={creating} onOpenChange={setCreating} />
    </div>
  );
}
